import { applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { createLogger } from 'redux-logger';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';

import analyticsApp, { getApplicationBackendUrl, getTokenOptions } from './reducers/analyticsApp';
import { loadSessionState as loadSessionStateDefault, saveSessionState } from './sessionStorage';
import {
  loadLocalStorageState as loadStorageStateDefault,
  saveLocalStorageState,
} from './localStorage';
import { loginInitialState, tokenOptionsInitial } from './reducers/login';
import { isValidTokens, throttledUpdateToken } from './actions/common';

const getInitialState = async () => {
  const sessionState = loadSessionStateDefault() || {};
  const storageState = loadStorageStateDefault() || {};

  const login = {
    ...(storageState.login
      ? {
          ...storageState.login,
          tokenOptions: {
            ...storageState.login.tokenOptions,
            clientTokenTime: Number(storageState.login.tokenOptions.clientTokenTime),
          },
        }
      : loginInitialState),
  };

  return {
    login,
    location: sessionState.location,
    previousLocation: sessionState.previousLocation,
    config: {
      backendUrl: storageState.backendUrl || '',
    },
  };
};

const refreshToken = (state) => (next) => async (action) => {
  const isValid = isValidTokens(getTokenOptions(state.getState()));
  const backendURL = getApplicationBackendUrl(state.getState());
  const tokenOptions = getTokenOptions(state.getState());
  const isToken = !!tokenOptions.accessToken.length && !!tokenOptions.refreshToken.length;

  if (typeof action === 'function' && !isValid && isToken) {
    await throttledUpdateToken(tokenOptions, backendURL, next);
    return next(action);
  }

  return next(action);
};

const configureStore = async () => {
  const middlewares = [refreshToken, thunk];

  if (process.env.NODE_ENV !== 'production') {
    const logger = createLogger({ collapsed: true });
    middlewares.push(logger);
  }

  const initialState = await getInitialState();

  const composeEnhancers = composeWithDevTools({
    trace: true,
    traceLimit: 10,
  });

  const enhancer = composeEnhancers(applyMiddleware(...middlewares));

  const store = createStore(analyticsApp, initialState, enhancer);

  store.subscribe(() => {
    saveLocalStorageState({
      login: {
        tenants: store.getState().login.tenants || [],
        username: store.getState().login.username || '',
        tokenOptions: store.getState().login.tokenOptions || tokenOptionsInitial,
        authenticated: store.getState().login.authenticated || false,
      },
      backendUrl: store.getState().config.backendUrl || '',
    });

    saveSessionState({
      location: store.getState().location || '',
      previousLocation: store.getState().previousLocation || '',
    });
  });

  window.onfocus = () => {
    const storageState = loadStorageStateDefault();
    if (
      !!storageState &&
      !!storageState.login &&
      storageState.login.username !== store.getState().login.username
    ) {
      document.location.reload();
    }
    if (!!storageState && !!storageState.login) {
      const login = {
        ...storageState.login,
      };
      store.dispatch({
        type: 'UPDATE_LOGIN_FROM_LOCAL_STORAGE',
        ...login,
      });
    }
  };

  return store;
};

export default configureStore;
